import { createContext, useContext, useEffect, ReactNode } from 'react'

/**
 * Brand Modes (cinematic lenses):
 *
 * A - Stealth Luxury: softer glass, less glow, more whitespace, minimal micro-labeling
 *     Best for: Home, Company, Pricing, general docs
 *
 * B - Technical Museum: chips, micro-labels, tighter grids, instrument-panel density
 *     Best for: Constellation, Architecture, Metrics, Governance
 *
 * C - Dream-Editorial: vignette, organic gradients, poetic spacing + rhythm
 *     Best for: About, Manifesto, launches, story content
 */
export type BrandMode = 'A' | 'B' | 'C'

interface BrandModeContextType {
  mode: BrandMode
}

const BrandModeContext = createContext<BrandModeContextType>({ mode: 'A' })

/**
 * Mode overlay tokens (CSS custom properties).
 * Core tokens (typography, spacing, radii, a11y contrast, motion timings)
 * remain stable. Only these deltas change per mode.
 */
const MODE_TOKENS: Record<BrandMode, Record<string, string>> = {
  A: {
    '--brand-surface-strength': '0.04',
    '--brand-glow-intensity': '0.08',
    '--brand-spacing-density': '1',
    '--brand-grid-gap': '2rem',
    '--brand-label-opacity': '0.35',
  },
  B: {
    '--brand-surface-strength': '0.08',
    '--brand-glow-intensity': '0.15',
    '--brand-spacing-density': '0.85',
    '--brand-grid-gap': '1.25rem',
    '--brand-label-opacity': '0.55',
  },
  C: {
    '--brand-surface-strength': '0.05',
    '--brand-glow-intensity': '0.12',
    '--brand-spacing-density': '1.15',
    '--brand-grid-gap': '2.5rem',
    '--brand-label-opacity': '0.40',
  },
}

interface BrandModeProviderProps {
  mode: BrandMode
  children: ReactNode
}

export function BrandModeProvider({ mode, children }: BrandModeProviderProps) {
  // Apply CSS custom property overrides and data attribute
  useEffect(() => {
    const root = document.documentElement
    const tokens = MODE_TOKENS[mode]

    // Set data attribute for CSS-only overrides
    root.setAttribute('data-brand-mode', mode)

    // Set CSS custom properties
    for (const [prop, value] of Object.entries(tokens)) {
      root.style.setProperty(prop, value)
    }

    return () => {
      // Cleanup on unmount
      root.removeAttribute('data-brand-mode')
      for (const prop of Object.keys(tokens)) {
        root.style.removeProperty(prop)
      }
    }
  }, [mode])

  return (
    <BrandModeContext.Provider value={{ mode }}>
      {children}
    </BrandModeContext.Provider>
  )
}

export function useBrandMode(): BrandModeContextType {
  return useContext(BrandModeContext)
}
